import { COMPACT_SLUGIFY_OPTIONS, Slugify } from '../SlugCompare';
import { toKebab, toTitleCase } from '../utilities';

/**
 * The flooring base item types of production CiC catalogs, with the product
 * type name Mongo catalogs use for each.
 */
const FLOORING_TYPES = [
  { code: 'covering.carpet', name: 'Carpet' },
  { code: 'covering.carpettile', name: 'Carpet Tile' },
  { code: 'covering.cork', name: 'Cork' },
  { code: 'covering.laminate', name: 'Laminate' },
  { code: 'covering.luxuryvinyl', name: 'Luxury Vinyl' },
  { code: 'covering.naturalstone', name: 'Natural Stone' },
  { code: 'covering.tile', name: 'Tile' },
  { code: 'covering.vinyl', name: 'Vinyl' },
  { code: 'covering.wood', name: 'Hardwood' },
  { code: 'accessory.rug', name: 'Area Rugs' },
];

/** SRP flag filters (`samples=1`) that are CiC merchandising codes, not characteristics. */
export const MERCHANDISING_FILTERS = [
  { name: 'Samples', code: 'websampled' },
  { name: 'Visualizable', code: 'webvisualizable' },
] as const;

/** Whether the values of an SRP flag filter turn it on: SRP links send `1`, the Fibi bot sends `true`. */
export function isFlagFilterEnabled(
  values: readonly string[] | null | undefined
): boolean {
  return values?.some((value) => value === 'true' || value === '1') ?? false;
}

const compactSlug = (value: string) => Slugify(value, COMPACT_SLUGIFY_OPTIONS);

/** Each flooring type under its code, its name and its code's last segment, as compact slugs. */
const FLOORING_LOOKUP = new Map(
  FLOORING_TYPES.flatMap((type) =>
    [type.code, type.name, type.code.split('.')[1]].map(
      (key) => [compactSlug(key), type] as const
    )
  )
);

/**
 * The CiC base item type for an SRP `type` value: a Mongo product type name
 * (`Luxury Vinyl`), its slug (`luxury-vinyl`, `luxuryvinyl`) or a CiC code.
 * A value that is not a known flooring type and has no `.` is taken as a
 * flooring type. CiC ignores case in these codes.
 */
export function toCiCBaseItemType(type: string): string {
  const known = FLOORING_LOOKUP.get(compactSlug(type));
  if (known) return known.code;
  return type.includes('.') ? type : `covering.${compactSlug(type)}`;
}

/** The product type name of a CiC base item type: `covering.wood` is `Hardwood`, `cabinetry.baseCabinet` is `Base Cabinet`. */
export function cicProductTypeName(baseItemType: string): string {
  return (
    FLOORING_LOOKUP.get(compactSlug(baseItemType))?.name ??
    toTitleCase(baseItemType.split('.').pop() ?? '')
  );
}

/**
 * The SRP `type` value of a CiC base item type, as SRP URLs and Mongo sites
 * use it: `covering.luxuryvinyl` is `luxury-vinyl`. KB&F codes
 * (`cabinetry.baseCabinet`) are kept whole, because their prefix selects how
 * the item is searched and shown.
 */
export function cicProductTypeSlug(baseItemType: string): string {
  const known = FLOORING_LOOKUP.get(compactSlug(baseItemType));
  if (known) return toKebab(known.name, true);
  if (!/^(covering|accessory)\./i.test(baseItemType)) return baseItemType;
  return toKebab(baseItemType.split('.').pop() ?? '', true);
}

/** The CiC merchandising code of an SRP tag: `in-stock` is `webinstock`. */
export function toCiCMerchandising(tag: string): string {
  return `web${compactSlug(tag)}`;
}

/**
 * The CiC characteristic code for an SRP query key that differs from the key
 * (`color-tones` is `colortone`); any other key is returned unchanged.
 */
export function cicCharacteristicCode(key: string): string {
  const aliases: Record<string, string> = {
    colortones: 'colortone',
    colorrange: 'colorfamily',
  };
  return aliases[compactSlug(key)] ?? key;
}

/**
 * Splits the merchandising codes of a CiC item (`webInStock`, `webSampled`)
 * into its display tags (`InStock`) and the sample and visualizer flags.
 */
export function cicMerchandisingTags(codes: readonly string[] = []) {
  const flags = codes.map(compactSlug);
  return {
    tags: codes
      .filter((_, i) => !MERCHANDISING_FILTERS.some((f) => f.code === flags[i]))
      .map((code) => code.replace(/^web/i, '')),
    hasSample: flags.includes('websampled'),
    hasVisualization: flags.includes('webvisualizable'),
  };
}
